import { BasicSelectField } from 'lib/iterable-hook-form/FormField/BasicSelectField';
import { InputField } from 'lib/iterable-hook-form/FormField/InputField';
import { Duration } from 'modules/workflowNodeEditors/components/formElements/DurationForm';
import { useFormContext, useWatch } from 'react-hook-form';
import styled from 'styled-components';
import { ErrorText } from 'ui/formField';
import { INPUT_TEXT_TYPES } from 'ui/inputs/constants';
import { getPlural } from '../formElements/DurationForm/DurationForm.utils';
import {
  CUSTOM_DURATION,
  CUSTOM_DURATION_VALUE,
  HOLD_DURATION,
  TIME_IN_MINUTES,
} from './HoldUntilReply.constants';
import {
  CustomDurationContainer,
  FormFieldDescription,
  FormFieldLabel,
  HoldDurationContainer,
} from './HoldUntilReply.styles';

const StyledInputField = styled(InputField)`
  flex: 1;
`;

export const HOLD_DURATION_TEST_ID = 'hold-duration-select';
export const CUSTOM_DURATION_DAYS_ID = 'custom-duration-days';
export const CUSTOM_DURATION_HOURS_ID = 'custom-duration-hours';
export const CUSTOM_DURATION_MINUTES_ID = 'custom-duration-minutes';

const MAX_TIME = {
  DAYS: 7,
  HOURS: 168,
  MINUTES: 10080,
};

const HOLD_DURATION_OPTIONS = [
  {
    id: '5minutes',
    label: '5 Minutes',
    value: TIME_IN_MINUTES.FIVE,
  },
  {
    id: '15minutes',
    label: '15 Minutes',
    value: TIME_IN_MINUTES.FIFTEEN,
  },
  {
    id: '1hour',
    label: '1 Hour',
    value: TIME_IN_MINUTES.HOUR,
  },
  {
    id: '24Hours',
    label: '24 Hours',
    value: TIME_IN_MINUTES.DAY,
  },
  {
    id: '3Days',
    label: '3 Days',
    value: TIME_IN_MINUTES.THREE_DAYS,
  },
  {
    id: '7Days',
    label: '7 Days',
    value: TIME_IN_MINUTES.WEEK,
  },
  {
    id: 'Custom',
    label: 'Custom',
    value: CUSTOM_DURATION_VALUE,
  },
];

export const HoldDuration = ({ isReadOnly }: { isReadOnly: boolean }) => {
  const {
    formState: { errors },
  } = useFormContext();
  const [selectedDuration, customDuration] = useWatch({ name: [HOLD_DURATION, CUSTOM_DURATION] });

  const customDurationErrorMessage = (errors[CUSTOM_DURATION]?.root?.message as string) || '';

  return (
    <HoldDurationContainer>
      <FormFieldLabel>Hold duration</FormFieldLabel>
      <FormFieldDescription>
        Specify how long a user should wait before moving forward in the journey
      </FormFieldDescription>
      <BasicSelectField
        automaticallyAvoidCollisions
        data-test={HOLD_DURATION_TEST_ID}
        isFullWidth
        isReadOnly={isReadOnly}
        name={HOLD_DURATION}
        options={HOLD_DURATION_OPTIONS}
        placeholder="Select a duration"
      />

      {/* Custom duration input fields */}
      {selectedDuration === CUSTOM_DURATION_VALUE && (
        <>
          <CustomDurationContainer>
            <StyledInputField
              id={CUSTOM_DURATION_DAYS_ID}
              data-test={CUSTOM_DURATION_DAYS_ID}
              defaultValue={0}
              isReadOnly={isReadOnly}
              placeholder="0"
              type={INPUT_TEXT_TYPES.NUMBER}
              Suffix={<span>{getPlural(customDuration.expireDays, Duration.Day)}</span>}
              minValue={0}
              maxValue={MAX_TIME.DAYS}
              name={`${CUSTOM_DURATION}.expireDays`}
              valueAsNumber
            />
            <StyledInputField
              id={CUSTOM_DURATION_HOURS_ID}
              data-test={CUSTOM_DURATION_HOURS_ID}
              defaultValue={0}
              isReadOnly={isReadOnly}
              placeholder="0"
              type={INPUT_TEXT_TYPES.NUMBER}
              Suffix={<span>{getPlural(customDuration.expireHours, Duration.Hour)}</span>}
              minValue={0}
              maxValue={MAX_TIME.HOURS}
              name={`${CUSTOM_DURATION}.expireHours`}
              valueAsNumber
            />
            <StyledInputField
              id={CUSTOM_DURATION_MINUTES_ID}
              data-test={CUSTOM_DURATION_MINUTES_ID}
              defaultValue={0}
              isReadOnly={isReadOnly}
              placeholder="0"
              type={INPUT_TEXT_TYPES.NUMBER}
              Suffix={<span>{getPlural(customDuration.expireMinutes, Duration.Minute)}</span>}
              minValue={0}
              maxValue={MAX_TIME.MINUTES}
              name={`${CUSTOM_DURATION}.expireMinutes`}
              valueAsNumber
            />
          </CustomDurationContainer>
          {!!customDurationErrorMessage && <ErrorText>{customDurationErrorMessage}</ErrorText>}
        </>
      )}
    </HoldDurationContainer>
  );
};
